import type {
  Bookmark,
  SimilarBookmark,
  MoveSuggestionsResponse,
  GroupSimilarResponse,
  BookmarkCounts,
  BookmarkFilter,
  BulkSelector,
  BulkFilterParams,
  BookmarksPage,
  Folder,
  Tag,
  SmartList,
  SmartListRules,
  AppUser,
  AiJob,
  AiJobSummary,
  AiOrganizeOptions,
  AiOrganizeProposal,
  AiOrganizeScope,
  AdminProvider,
  AdminRateLimit,
  AdminRequestLimit,
  AdminTokenAdjustResult,
  AdminUser,
  AdminUserBookmark,
  AdminAnomalyFlag,
  AnomalyRunSummary,
  AdminAlertConfig,
  AdminAlertTestResult,
  AlertCategory,
  AlertChannel,
  UserRole,
  AccountTransaction,
  TokenBalance,
  ApiTokenCreated,
  ApiTokenScope,
  ApiTokenSummary,
  CatalogCategory,
  CatalogEntry,
  CatalogMetadataAudit,
  CatalogTitleSuggestion,
  DiscoverResult,
  Job,
  LatestExtensionReleases,
  HtmlImportJob,
  HtmlImportVariant,
  HtmlImportPreview,
  Space,
  AnniversaryItem,
  AskAnswer,
  AskHistoryMessage,
  AskConversation,
  DeadLinksResponse,
  EmptyFoldersResponse,
  WaybackMode,
} from '../types';
import type {
  AiFeatureSettingsMap,
  BackupPrefs,
  GithubSyncPrefs,
  ClassificationPrefs,
  ProviderSettingsOverrides,
  PublicAppSettings,
  ResurfacePrefs,
  SuggestResult,
  UiPrefs,
} from '../types';
import type { BookmarksSortOption } from './bookmarks-sort';
import type { DuplicatesResponse } from './duplicates/types';

const OPTS: RequestInit = { credentials: 'include' };

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { ...OPTS, ...init });
  if (!res.ok) {
    let code: string | undefined;
    let serverMsg: string | undefined;
    try {
      const body = (await res.clone().json()) as { error?: string; code?: string };
      code = body?.code;
      serverMsg = body?.error;
    } catch {
      // non-JSON error body — stick with the status-line message
    }
    throw new ApiError(
      res.status,
      serverMsg ?? `${init?.method ?? 'GET'} ${path} → ${res.status}`,
      code,
    );
  }
  return res.json() as Promise<T>;
}

function json(body: unknown): RequestInit {
  return {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

// MF-218 — masked status of a stored secret (e.g. the GitHub PAT). Mirrors the
// server's SecretStatus; updatedAt arrives as an ISO string over JSON.
export interface SecretStatusDto {
  connected: boolean;
  last4: string | null;
  updatedAt: string | null;
}

// Flexible Select All — maps a BookmarkFilter (+ current Space) to the bulk
// selector's filter params, the same view→params mapping getBookmarks does for
// its query string. Used to build a `{ filter, excludeIds }` selector when the
// user picks "Select all N".
export function bookmarkFilterToParams(
  filter: BookmarkFilter,
  spaceId?: string | null,
): BulkFilterParams {
  const params: BulkFilterParams = {};
  switch (filter.type) {
    case 'favorites':
      params.isFavorite = true;
      break;
    case 'unsorted':
      params.unsorted = true;
      break;
    case 'folder':
      params.folderId = filter.folderId;
      break;
    case 'tag':
      params.tagId = filter.tagId;
      break;
    case 'search':
      params.q = filter.q;
      break;
    case 'source':
      params.source = filter.source;
      break;
    case 'global':
      params.global = true;
      break;
    case 'smart':
      // Server scopes a smart list to its own Space; spaceId is ignored there.
      params.smartListId = filter.smartListId;
      break;
    case 'all':
    default:
      break;
  }
  if (spaceId) params.spaceId = spaceId;
  return params;
}

// Coalesce concurrent auth probes. The logged-out landing mounts App +
// PublicShell + several <PublicCtaButtons> (hero, final CTA…), each of which
// independently calls getMe() in its mount effect — ~8 identical /auth/me
// requests fired in the same tick. Share one in-flight request; clear it once
// it settles so later (e.g. post-navigation) probes still fetch fresh state.
let inFlightMe: Promise<AppUser | null> | null = null;

export const api = {
  // Auth
  getMe: (): Promise<AppUser | null> => {
    // Logged-out landing: the server (session ground truth) baked
    // __MF_LOGGED_OUT__ into the prerendered HTML. Skip the /auth/me fetch — on
    // this document it can only resolve to null, yet it's the tail of the
    // landing's critical request chain. Do NOT clear the flag: the landing
    // mounts ~8 independent getMe() callers (App, PublicShell, each
    // PublicCtaButtons), and clearing on the first read lets the second fall
    // through to a real fetch. The flag can't go stale — login/logout hard-
    // reload the document (window.location), so a fresh doc always re-derives it.
    const w = window as unknown as { __MF_LOGGED_OUT__?: boolean };
    if (w.__MF_LOGGED_OUT__) return Promise.resolve(null);
    if (inFlightMe) return inFlightMe;
    inFlightMe = fetch('/auth/me', OPTS)
      // Server answers 200 + `null` when logged out (avoids a console-error 401).
      .then((r) => (r.ok ? (r.json() as Promise<AppUser | null>) : null))
      .finally(() => {
        inFlightMe = null;
      });
    return inFlightMe;
  },

  logout: (): Promise<void> =>
    fetch('/auth/logout', { ...OPTS, method: 'POST' }).then(() => undefined),

  resendVerification: (): Promise<{ ok: true }> =>
    request('/auth/verify/resend', { method: 'POST' }),

  // MF-116 — auth provider availability (login/signup button gating).
  getAuthProviders: (): Promise<{ google: boolean; github: boolean }> =>
    request('/auth/providers'),

  // Magic link request — neutral {ok:true} regardless of account existence.
  magicRequest: (email: string, lang?: string): Promise<{ ok: true }> =>
    request('/auth/magic', { method: 'POST', ...json({ email, lang }) }),

  // Email/password login — returns the session user on success, throws ApiError(401) otherwise.
  passwordLogin: (email: string, password: string, lang?: string): Promise<AppUser> =>
    request('/auth/password/login', { method: 'POST', ...json({ email, password, lang }) }),

  // Email/password signup — returns the session user; throws 409 email_taken / 400 weak_password.
  passwordSignup: (email: string, password: string, name?: string, lang?: string): Promise<AppUser> =>
    request('/auth/password/signup', { method: 'POST', ...json({ email, password, name, lang }) }),

  // Password reset request — neutral {ok:true}.
  requestPasswordReset: (email: string, lang?: string): Promise<{ ok: true }> =>
    request('/auth/password/reset/request', { method: 'POST', ...json({ email, lang }) }),

  // Complete password reset — returns the freshly-logged-in user; throws 400 token_invalid / weak_password.
  confirmPasswordReset: (token: string, password: string): Promise<AppUser> =>
    request('/auth/password/reset/confirm', { method: 'POST', ...json({ token, password }) }),

  // Security tab — linked methods + connected-since.
  getAuthMethods: (): Promise<{
    email: string;
    magicLinkAvailable: boolean;
    methods: { provider: string; email: string | null; connectedAt: string; lastLoginAt: string | null }[];
  }> => request('/api/me/auth-methods'),

  // Set (OAuth user) or change (current required) the password.
  savePassword: (newPassword: string, currentPassword?: string): Promise<{ ok: true }> =>
    request('/api/me/auth-methods/password', { method: 'POST', ...json({ newPassword, currentPassword }) }),

  // Unlink a stored provider (≥1-method guard → 409 cannot_remove_last_method).
  unlinkMethod: (provider: string): Promise<{ ok: true }> =>
    request(`/api/me/auth-methods/${encodeURIComponent(provider)}`, { method: 'DELETE' }),

  // Request an email change — confirm link goes to the new address.
  requestEmailChange: (newEmail: string, lang?: string): Promise<{ ok: true }> =>
    request('/api/me/email/change/request', { method: 'POST', ...json({ newEmail, lang }) }),

  // Sign out of all other sessions.
  revokeAllSessions: (): Promise<{ ok: true }> =>
    request('/api/me/sessions/revoke-all', { method: 'POST' }),

  getClassificationPrefs: (): Promise<ClassificationPrefs> =>
    request('/api/me/classification-prefs'),

  setClassificationPrefs: (prefs: ClassificationPrefs): Promise<ClassificationPrefs> =>
    request('/api/me/classification-prefs', { method: 'PUT', ...json(prefs) }),

  // MF-111 — per-user visual-customization prefs (theme / accent / grid size).
  // setUiPrefs takes a partial patch; the server merges + returns the full prefs.
  getUiPrefs: (): Promise<UiPrefs> => request('/api/me/ui-prefs'),
  setUiPrefs: (patch: Partial<UiPrefs>): Promise<UiPrefs> =>
    request('/api/me/ui-prefs', { method: 'PUT', ...json(patch) }),

  getResurface: (): Promise<{ prefs: ResurfacePrefs; timezone: string | null }> =>
    request('/api/me/resurface'),
  setResurfacePrefs: (prefs: ResurfacePrefs): Promise<ResurfacePrefs> =>
    request('/api/me/resurface', { method: 'PUT', ...json(prefs) }),
  setTimezone: (timezone: string): Promise<{ timezone: string }> =>
    request('/api/me/timezone', { method: 'PUT', ...json({ timezone }) }),

  // MF-215 — periodic backup-email schedule prefs.
  getBackup: (): Promise<{ prefs: BackupPrefs; timezone: string | null }> =>
    request('/api/me/backups'),
  setBackupPrefs: (prefs: BackupPrefs): Promise<BackupPrefs> =>
    request('/api/me/backups', { method: 'PUT', ...json(prefs) }),

  // MF-218 — GitHub markdown sync. PAT is write-only (only last4 returned).
  getGithubSync: (): Promise<{ prefs: GithubSyncPrefs; timezone: string | null; token: SecretStatusDto }> =>
    request('/api/me/github-sync'),
  setGithubSyncPrefs: (prefs: GithubSyncPrefs): Promise<GithubSyncPrefs> =>
    request('/api/me/github-sync', { method: 'PUT', ...json(prefs) }),
  setGithubPat: (pat: string): Promise<SecretStatusDto> =>
    request('/api/me/github-sync/pat', { method: 'PUT', ...json({ pat }) }),
  deleteGithubPat: (): Promise<SecretStatusDto> =>
    request('/api/me/github-sync/pat', { method: 'DELETE' }),
  testGithubSync: (): Promise<{ ok: boolean; canPush: boolean; defaultBranch: string }> =>
    request('/api/me/github-sync/test', { method: 'POST' }),
  runGithubSync: (): Promise<{ jobId: string }> =>
    request('/api/me/github-sync/run', { method: 'POST' }),

  // MF-141 — anniversary "on this day" strip.
  getAnniversary: (spaceId: string | null): Promise<{ items: AnniversaryItem[] }> =>
    request(`/api/bookmarks/anniversary${spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : ''}`),
  markAnniversaryOpened: (logId: string): Promise<{ ok: boolean }> =>
    request(`/api/bookmarks/anniversary/${encodeURIComponent(logId)}/open`, { method: 'POST' }),

  // MF-190 — Spaces post-migration onboarding flag (null = banner due).
  getSpacesOnboarding: (): Promise<{ onboardedAt: string | null }> =>
    request('/api/me/spaces-onboarding'),

  dismissSpacesOnboarding: (): Promise<{ onboardedAt: string | null }> =>
    request('/api/me/spaces-onboarding/dismiss', { method: 'POST' }),

  getAppSettings: (): Promise<PublicAppSettings> =>
    request('/api/app-settings'),

  // MF-199 — resolved feature flags for the current user.
  getFeatureFlags: (): Promise<Record<string, boolean>> =>
    request('/api/me/features'),

  // MF-130 — enqueue an on-demand full-account backup; the link arrives by email.
  requestExport: (): Promise<{ jobId: string }> =>
    request('/api/me/export', { method: 'POST' }),

  // Bookmarks
  getBookmarks: (opts: {
    sort: BookmarksSortOption;
    offset: number;
    limit: number;
    filter: BookmarkFilter;
    spaceId?: string;
  }): Promise<BookmarksPage> => {
    const qs = new URLSearchParams();
    qs.set('sort', opts.sort);
    qs.set('offset', String(opts.offset));
    qs.set('limit', String(opts.limit));
    switch (opts.filter.type) {
      case 'favorites':
        qs.set('isFavorite', 'true');
        break;
      case 'unsorted':
        qs.set('unsorted', 'true');
        break;
      case 'folder':
        qs.set('folderId', opts.filter.folderId);
        break;
      case 'tag':
        qs.set('tagId', opts.filter.tagId);
        break;
      case 'search':
        qs.set('q', opts.filter.q);
        break;
      case 'source':
        qs.set('source', opts.filter.source);
        break;
      case 'global':
        qs.set('global', 'true');
        break;
      case 'smart':
        // MF-156 — server loads the list's rules + its own Space scope.
        qs.set('smartListId', opts.filter.smartListId);
        break;
      case 'all':
      default:
        break;
    }
    if (opts.spaceId) qs.set('spaceId', opts.spaceId);
    return request(`/api/bookmarks?${qs.toString()}`);
  },

  // MF-165 — single owner-scoped bookmark (Smart List rule builder resolves a
  // saved similar_to seed's title in edit mode).
  getBookmark: (id: string): Promise<Bookmark> =>
    request(`/api/bookmarks/${encodeURIComponent(id)}`),

  getBookmarkCounts: (spaceId?: string): Promise<BookmarkCounts> =>
    request(`/api/bookmarks/counts${spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : ''}`),

  createBookmark: (data: {
    url: string;
    title: string;
    description?: string;
    image?: string;
    folderId?: string | null;
    tagIds: string[];
    isFavorite?: boolean;
    spaceId?: string;
    // MF-168 — echo the tag-suggestion log id + whether top-1 was auto-applied
    // so the server can record which suggested tags were kept (admin stats).
    tagSuggestLogId?: string;
    tagSuggestAutoApplied?: boolean;
  }): Promise<Bookmark> =>
    request('/api/bookmarks', { method: 'POST', ...json(data) }),

  bulkAddBookmarks: (
    urls: string[],
    folderId?: string | null,
    spaceId?: string,
  ): Promise<{ jobId: string }> =>
    request('/api/bookmarks/bulk', { method: 'POST', ...json({ urls, folderId, spaceId }) }),

  importBookmarks: (
    items: Array<{ url: string; title: string; folder?: string }>,
    spaceId?: string,
  ): Promise<{ jobId: string }> =>
    request('/api/bookmarks/import', { method: 'POST', ...json({ bookmarks: items, spaceId }) }),

  updateBookmark: (
    id: string,
    patch: {
      isFavorite?: boolean;
      folderId?: string | null;
      title?: string;
      description?: string;
      tagIds?: string[];
      url?: string;
      image?: string;
      isGlobal?: boolean;
      spaceId?: string;
    },
  ): Promise<Bookmark> =>
    request(`/api/bookmarks/${id}`, { method: 'PATCH', ...json(patch) }),

  deleteBookmark: (id: string): Promise<void> =>
    request(`/api/bookmarks/${id}`, { method: 'DELETE' }),

  // Fire-and-forget visit ping for popularity sort. Server dedupes within 5s
  // per (user, bookmark), so spamming the call is safe; navigation must not
  // block on this — call without awaiting.
  recordBookmarkVisit: (id: string, source: 'web' | 'ext' = 'web'): Promise<{ ok: true }> =>
    request(`/api/bookmarks/${id}/visit`, { method: 'POST', ...json({ source }) }),

  // Flexible Select All — every bulk op takes a selector (explicit ids OR the
  // active filter + exclusions); the server resolves the filter to ids.
  bulkDeleteBookmarks: (selector: BulkSelector): Promise<{ deleted: number }> =>
    request('/api/bookmarks/bulk-delete', { method: 'POST', ...json(selector) }),

  bulkUpdateBookmarks: (
    selector: BulkSelector,
    patch: { isFavorite?: boolean; folderId?: string | null },
  ): Promise<{ updated: number }> =>
    request('/api/bookmarks/bulk-update', { method: 'POST', ...json({ ...selector, patch }) }),

  bulkTagBookmarks: (
    selector: BulkSelector,
    addTagId?: string,
    removeTagId?: string,
  ): Promise<{ ok: true }> =>
    request('/api/bookmarks/bulk-tag', {
      method: 'POST',
      ...json({ ...selector, addTagId, removeTagId }),
    }),

  // MF-160 — pin keeps a bookmark at the top of its folder (per-bookmark flag).
  pinBookmark: (id: string): Promise<{ ok: true; bookmarkId: string }> =>
    request(`/api/bookmarks/${id}/pin`, { method: 'POST', ...json({}) }),

  unpinBookmark: (id: string): Promise<{ ok: true }> =>
    request(`/api/bookmarks/${id}/pin`, { method: 'DELETE' }),

  inferBookmarkTitle: (
    id: string,
    opts?: { force?: boolean; uiLang?: string },
  ): Promise<{ bookmark: Bookmark; balance: TokenBalance }> =>
    request(`/api/bookmarks/${id}/infer-title`, { method: 'POST', ...json(opts ?? {}) }),

  inferBookmarkDescription: (
    id: string,
    opts?: { force?: boolean; uiLang?: string; pageText?: string },
  ): Promise<{ bookmark: Bookmark; balance: TokenBalance }> =>
    request(`/api/bookmarks/${id}/describe`, { method: 'POST', ...json(opts ?? {}) }),

  suggestForBookmark: (data: {
    url: string;
    title?: string;
    description?: string;
    spaceId?: string;
  }): Promise<SuggestResult> =>
    request('/api/bookmarks/suggest', { method: 'POST', ...json(data) }),

  // MF-169 — thumbs-down on the "why this AI suggestion" tooltip. Best-effort;
  // the UI fires it fire-and-forget and shows a "thanks" state on resolve.
  logSuggestionFeedback: (data: {
    feature: 'folder' | 'tag';
    decision?: string;
    provider?: string | null;
    logId?: string;
    targetId?: string;
    similarity?: number;
  }): Promise<{ ok: true }> =>
    request('/api/bookmarks/suggest/feedback', { method: 'POST', ...json(data) }),

  // MF-157 — "Find similar" k-NN neighbors of a bookmark. scope defaults to the
  // bookmark's Space; 'all' widens the pool to every Space.
  getSimilarBookmarks: (
    id: string,
    scope: 'space' | 'all' = 'space',
  ): Promise<{ items: SimilarBookmark[]; scope: 'space' | 'all' }> =>
    request(`/api/bookmarks/${encodeURIComponent(id)}/similar?scope=${scope}`),

  // MF-103 surface #2 — batch "where do similar bookmarks live" for inline move
  // chips on the Unsorted view. Keyed by bookmark id; ids with no confident
  // destination are simply omitted from the map.
  getMoveSuggestions: (
    ids: string[],
    scope: 'space' | 'all' = 'space',
  ): Promise<MoveSuggestionsResponse> =>
    request('/api/bookmarks/move-suggestions', { method: 'POST', ...json({ ids, scope }) }),

  // MF-103 surface #3 — ad-hoc clustering of the Unsorted set into preview groups
  // (no LLM, no folder creation; the client disposes each group).
  groupSimilar: (spaceId?: string): Promise<GroupSimilarResponse> =>
    request('/api/bookmarks/group-similar', { method: 'POST', ...json({ spaceId }) }),

  // Discover
  getDiscover: (perCategory?: number): Promise<DiscoverResult> =>
    request(`/api/discover${perCategory ? `?perCategory=${perCategory}` : ''}`),

  getFolderSuggested: (folderId: string, limit?: number): Promise<DiscoverResult> =>
    request(`/api/folders/${folderId}/suggested${limit ? `?limit=${limit}` : ''}`),

  // Folders
  getFolders: (all?: boolean, spaceId?: string): Promise<{ items: Folder[]; total: number }> => {
    const qs = new URLSearchParams();
    if (all) qs.set('all', 'true');
    if (spaceId) qs.set('spaceId', spaceId);
    const s = qs.toString();
    return request(`/api/folders${s ? `?${s}` : ''}`);
  },

  createFolder: (
    name: string,
    parentId?: string | null,
    color?: string | null,
    spaceId?: string,
    emoji?: string | null,
  ): Promise<Folder> =>
    request('/api/folders', { method: 'POST', ...json({ name, parentId, color, spaceId, emoji }) }),

  updateFolder: (
    id: string,
    patch: { name?: string; color?: string | null; emoji?: string | null; parentId?: string | null },
  ): Promise<Folder> =>
    request(`/api/folders/${id}`, { method: 'PATCH', ...json(patch) }),

  deleteFolder: (
    id: string,
    opts?: { mode?: 'unset' | 'cascade' | 'move'; targetId?: string | null },
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (opts?.mode) params.set('mode', opts.mode);
    if (opts?.targetId) params.set('targetId', opts.targetId);
    const qs = params.toString();
    return request(`/api/folders/${id}${qs ? `?${qs}` : ''}`, { method: 'DELETE' });
  },

  bulkUpdateFolders: (ids: string[], patch: { color?: string | null }): Promise<void> =>
    request('/api/folders/bulk-update', { method: 'POST', ...json({ ids, patch }) }),

  bulkDeleteFolders: (ids: string[]): Promise<void> =>
    request('/api/folders/bulk-delete', { method: 'POST', ...json({ ids }) }),

  // Spaces (MF-185)
  getSpaces: (): Promise<{ items: Space[] }> => request('/api/spaces'),

  createSpace: (data: {
    name: string;
    slug?: string;
    color?: string;
    icon?: string | null;
  }): Promise<Space> =>
    request('/api/spaces', { method: 'POST', ...json(data) }),

  updateSpace: (
    id: string,
    patch: {
      name?: string;
      color?: string | null;
      icon?: string | null;
      defaultView?: 'grid' | 'list' | 'masonry';
      sortOrder?: number;
      aiSettings?: import('../types').SpaceAiSettings;
    },
  ): Promise<Space> =>
    request(`/api/spaces/${id}`, { method: 'PATCH', ...json(patch) }),

  deleteSpace: (id: string, reassignTo?: string): Promise<void> =>
    request(`/api/spaces/${id}`, {
      method: 'DELETE',
      ...(reassignTo ? json({ reassignTo }) : {}),
    }),

  // MF-194 — promote a Space to default. Server clears the prior default in
  // the same transaction so the partial unique index never trips.
  setDefaultSpace: (id: string): Promise<{ ok: true }> =>
    request(`/api/spaces/${id}/default`, { method: 'PATCH' }),

  // Tags
  getTags: (all?: boolean, spaceId?: string): Promise<{ items: Tag[]; total: number }> => {
    const qs = new URLSearchParams();
    if (all) qs.set('all', 'true');
    if (spaceId) qs.set('spaceId', spaceId);
    const s = qs.toString();
    return request(`/api/tags${s ? `?${s}` : ''}`);
  },

  createTag: (name: string, color?: string | null, spaceId?: string): Promise<Tag> =>
    request('/api/tags', { method: 'POST', ...json({ name, color, spaceId }) }),

  // Smart Lists (MF-156)
  getSmartLists: (spaceId?: string): Promise<{ items: SmartList[]; total: number }> => {
    const qs = new URLSearchParams();
    if (spaceId) qs.set('spaceId', spaceId);
    const s = qs.toString();
    return request(`/api/smart-lists${s ? `?${s}` : ''}`);
  },

  createSmartList: (data: {
    name: string;
    color?: string | null;
    rules: SmartListRules;
    spaceId?: string;
  }): Promise<SmartList> =>
    request('/api/smart-lists', { method: 'POST', ...json(data) }),

  updateSmartList: (
    id: string,
    patch: { name?: string; color?: string | null; rules?: SmartListRules },
  ): Promise<SmartList> =>
    request(`/api/smart-lists/${id}`, { method: 'PATCH', ...json(patch) }),

  deleteSmartList: (id: string): Promise<void> =>
    request(`/api/smart-lists/${id}`, { method: 'DELETE' }),

  reorderSmartLists: (ids: string[]): Promise<{ ok: true; updated: number }> =>
    request('/api/smart-lists/reorder', { method: 'POST', ...json({ ids }) }),

  updateTag: (id: string, patch: { name?: string; color?: string | null }): Promise<Tag> =>
    request(`/api/tags/${id}`, { method: 'PATCH', ...json(patch) }),

  deleteTag: (id: string): Promise<void> =>
    request(`/api/tags/${id}`, { method: 'DELETE' }),

  bulkUpdateTags: (ids: string[], patch: { color?: string | null }): Promise<void> =>
    request('/api/tags/bulk-update', { method: 'POST', ...json({ ids, patch }) }),

  bulkDeleteTags: (ids: string[]): Promise<void> =>
    request('/api/tags/bulk-delete', { method: 'POST', ...json({ ids }) }),

  // AI
  getAiStatus: (): Promise<{ available: boolean; titleInfer: boolean }> => request('/api/ai/status'),

  startAiOrganize: (
    scope: AiOrganizeScope,
    uiLang: string,
    options: AiOrganizeOptions = {},
    spaceId?: string,
  ): Promise<AiJob> =>
    request('/api/ai/organize', { method: 'POST', ...json({ scope, uiLang, options, spaceId }) }),

  listAiJobs: (): Promise<AiJobSummary[]> => request('/api/ai/jobs'),

  getAiJob: (id: string): Promise<AiJob> => request(`/api/ai/jobs/${id}`),

  patchAiJob: (id: string, editedProposal: AiOrganizeProposal): Promise<AiJob> =>
    request(`/api/ai/jobs/${id}`, { method: 'PATCH', ...json({ editedProposal }) }),

  resetAiJob: (id: string): Promise<AiJob> =>
    request(`/api/ai/jobs/${id}/reset`, { method: 'POST' }),

  discardAiJob: async (id: string): Promise<void> => {
    const res = await fetch(`/api/ai/jobs/${id}/discard`, { ...OPTS, method: 'POST' });
    if (!res.ok) throw new ApiError(res.status, `Discard failed (${res.status})`);
  },

  applyAiJob: (
    id: string,
  ): Promise<{
    createdFolders: number;
    movedBookmarks: number;
    unclassified: number;
    taggedBookmarks: number;
  }> => request(`/api/ai/jobs/${id}/apply`, { method: 'POST', ...json({}) }),

  // MF-123 — Ask MarkFlow RAG. MF-195: spaceId required on send + conversations.
  ask: {
    send: (body: {
      query: string;
      conversationId: string;
      spaceId: string;
      scope?: { folderId?: string; tagIds?: string[] };
      uiLang?: string;
    }): Promise<AskAnswer> => request('/api/ai/chat-rag', { method: 'POST', ...json(body) }),

    history: (conversationId: string): Promise<AskHistoryMessage[]> =>
      request(`/api/ai/chat-rag/history?conversationId=${encodeURIComponent(conversationId)}`),

    conversations: (spaceId: string, q?: string): Promise<AskConversation[]> => {
      const params = new URLSearchParams({ spaceId });
      if (q && q.trim()) params.set('q', q.trim());
      return request(`/api/ai/chat-rag/conversations?${params.toString()}`);
    },

    deleteConversation: (conversationId: string): Promise<{ deletedCount: number }> =>
      request(`/api/ai/chat-rag/conversations/${encodeURIComponent(conversationId)}`, {
        method: 'DELETE',
      }),
  },

  // Account
  exportAccount: async (): Promise<Blob> => {
    const res = await fetch('/api/account/export', OPTS);
    if (!res.ok) throw new Error(`GET /api/account/export → ${res.status}`);
    return res.blob();
  },

  exportBookmarks: async (params: {
    format: 'json' | 'html';
    spaceId?: string;
    folders?: string[];
    tags?: string[];
    from?: string;
    to?: string;
    favorites?: boolean;
    withDesc?: boolean;
    q?: string;
    gzip?: boolean;
  }): Promise<Blob> => {
    const qs = new URLSearchParams();
    qs.set('format', params.format);
    if (params.spaceId) qs.set('spaceId', params.spaceId);
    for (const f of params.folders ?? []) qs.append('folders[]', f);
    for (const t of params.tags ?? []) qs.append('tags[]', t);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.favorites) qs.set('favorites', 'true');
    if (params.withDesc) qs.set('with_desc', 'true');
    if (params.q) qs.set('q', params.q);
    if (params.gzip) qs.set('gzip', '1');
    const res = await fetch(`/api/bookmarks/export?${qs.toString()}`, OPTS);
    if (!res.ok) throw new Error(`GET /api/bookmarks/export → ${res.status}`);
    return res.blob();
  },

  exportBookmarksCount: async (params: {
    format: 'json' | 'html';
    spaceId?: string;
    folders?: string[];
    tags?: string[];
    from?: string;
    to?: string;
    favorites?: boolean;
    withDesc?: boolean;
    q?: string;
  }): Promise<{ count: number; estimatedBytes: number }> => {
    const qs = new URLSearchParams();
    qs.set('format', params.format);
    qs.set('countOnly', '1');
    if (params.spaceId) qs.set('spaceId', params.spaceId);
    for (const f of params.folders ?? []) qs.append('folders[]', f);
    for (const t of params.tags ?? []) qs.append('tags[]', t);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.favorites) qs.set('favorites', 'true');
    if (params.withDesc) qs.set('with_desc', 'true');
    if (params.q) qs.set('q', params.q);
    const res = await fetch(`/api/bookmarks/export?${qs.toString()}`, OPTS);
    if (!res.ok) throw new Error(`GET /api/bookmarks/export(count) → ${res.status}`);
    return res.json();
  },

  deleteAccount: async (): Promise<void> => {
    const res = await fetch('/api/account', { ...OPTS, method: 'DELETE' });
    if (!res.ok) throw new Error(`DELETE /api/account → ${res.status}`);
  },

  // Jobs
  jobs: {
    list: (params?: { status?: 'active' | 'recent' | 'failed'; limit?: number; offset?: number }): Promise<{ items: Job[]; total: number }> => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.limit != null) qs.set('limit', String(params.limit));
      if (params?.offset != null) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request(`/api/jobs${suffix}`);
    },

    get: (id: string): Promise<Job> => request(`/api/jobs/${id}`),

    cancel: (id: string): Promise<{ ok: true }> =>
      request(`/api/jobs/${id}`, { method: 'DELETE' }),

    createMetaRefetch: (selector: BulkSelector): Promise<Job> =>
      request('/api/jobs/meta-refetch', { method: 'POST', ...json(selector) }),
  },

  // Admin
  admin: {
    listProviders: (): Promise<AdminProvider[]> => request('/api/admin/ai-providers'),

    createProvider: (data: {
      feature: 'embed' | 'chat' | 'title_infer' | 'description_infer' | 'classify_tie_break' | 'chat_rag';
      provider: 'gemini' | 'cloudflare' | 'ollama' | 'voyage' | 'groq' | 'clod' | 'deepinfra';
      priority: number;
      isEnabled: boolean;
      config: Record<string, unknown>;
    }): Promise<AdminProvider> =>
      request('/api/admin/ai-providers', { method: 'POST', ...json(data) }),

    updateProvider: (
      id: string,
      patch: {
        priority?: number;
        isEnabled?: boolean;
        config?: Record<string, unknown>;
        rateLimit?: { rpm: number; concurrency: number; tpm?: number; rpd?: number };
        circuitBreaker?: { openAfterConsecutiveFailures: number; openCooldownSec: number; halfOpenProbeCount: number };
        settingsOverrides?: ProviderSettingsOverrides;
      },
    ): Promise<AdminProvider> =>
      request(`/api/admin/ai-providers/${id}`, { method: 'PUT', ...json(patch) }),

    deleteProvider: (id: string): Promise<void> =>
      request(`/api/admin/ai-providers/${id}`, { method: 'DELETE' }),

    getAiQueues: (): Promise<{
      status: 'connected' | 'disabled' | 'connecting' | 'reconnecting' | 'closed';
      streams: Array<{
        name: string;
        feature: 'embed' | 'chat' | 'title_infer' | 'description_infer' | 'classify_tie_break' | 'meta' | 'ai_organize' | 'jobs';
        messages?: number;
        bytes?: number;
        consumers?: number;
        firstSeq?: number;
        lastSeq?: number;
        missing?: boolean;
      }>;
      consumers: Array<{
        feature: string;
        provider: string;
        effectiveRpm: number;
        circuitState: 'closed' | 'open' | 'half_open';
        inFlight: number;
        successCount: number;
        failureCount: number;
        republishCount: number;
        lastError?: { ts: number; message: string };
        recentErrors: Array<{ ts: number; message: string }>;
      }>;
      dlqCount: number;
      meta: {
        inFlight: number;
        successCount: number;
        failureCount: number;
        skippedCount: number;
        dlqCount: number;
        activeDomains: number;
        lastError?: { ts: number; message: string };
        recentErrors: Array<{ ts: number; message: string }>;
      } | null;
      aiOrganize: {
        inFlight: number;
        successCount: number;
        failureCount: number;
        skippedCount: number;
        nakCount: number;
        activeUsers: number;
        lastError?: { ts: number; message: string };
        recentErrors: Array<{ ts: number; message: string }>;
      } | null;
      jobsConsumers: Array<{
        type: 'bulk_add' | 'import' | 'meta_refetch' | 'catalog_import';
        concurrency: number;
        inFlight: number;
        successCount: number;
        failureCount: number;
        skippedCount: number;
        nakCount: number;
        activeUsers: number;
        lastError?: { ts: number; message: string };
        recentErrors: Array<{ ts: number; message: string }>;
      }>;
    }> => request('/api/admin/ai-queues'),

    listAiQueueDlq: (limit?: number): Promise<{
      items: Array<{
        // null when the row originated from a live WS `ai_dlq_added` push and
        // a stable JetStream seq is not yet known on the client. REST always
        // returns a number; WS-pushed rows render but Retry/Discard are gated.
        seq: number | null;
        feature: string;
        payload: unknown;
        tried: string[];
        attempt: number;
        userId?: string;
        refKind?: string;
        refId?: string;
        lastError: string;
        ts: string;
      }>;
    }> => {
      const qs = limit ? `?limit=${limit}` : '';
      return request(`/api/admin/ai-queues/dlq${qs}`);
    },

    retryAiQueueDlq: (seq: number): Promise<{ ok: true }> =>
      request(`/api/admin/ai-queues/dlq/${seq}/retry`, { method: 'POST' }),

    discardAiQueueDlq: (seq: number): Promise<{ ok: true }> =>
      request(`/api/admin/ai-queues/dlq/${seq}`, { method: 'DELETE' }),

    listUsers: (params?: { q?: string; limit?: number; offset?: number }): Promise<{ items: AdminUser[]; total: number }> => {
      const qs = new URLSearchParams();
      if (params?.q) qs.set('q', params.q);
      if (params?.limit != null) qs.set('limit', String(params.limit));
      if (params?.offset != null) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request(`/api/admin/users${suffix}`);
    },

    getUser: (id: string): Promise<AdminUser> => request(`/api/admin/users/${id}`),

    listUserBookmarks: (
      id: string,
      params?: { limit?: number; offset?: number },
    ): Promise<{ items: AdminUserBookmark[]; total: number }> => {
      const qs = new URLSearchParams();
      if (params?.limit != null) qs.set('limit', String(params.limit));
      if (params?.offset != null) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request(`/api/admin/users/${id}/bookmarks${suffix}`);
    },

    deleteUser: (id: string, confirmEmail: string): Promise<{ ok: true; deletedEmail: string }> =>
      request(`/api/admin/users/${id}`, { method: 'DELETE', ...json({ confirmEmail }) }),

    listUserTokens: (id: string): Promise<ApiTokenSummary[]> =>
      request(`/api/admin/users/${id}/tokens`),

    revokeUserToken: (id: string, tokenId: string): Promise<{ ok: true }> =>
      request(`/api/admin/users/${id}/tokens/${tokenId}`, { method: 'DELETE' }),

    bulkAdjustTokens: (data: {
      userIds: string[];
      deltaTokens: number;
      note: string;
    }): Promise<AdminTokenAdjustResult> =>
      request('/api/admin/users/bulk-token-adjust', { method: 'POST', ...json(data) }),

    listRateLimits: (): Promise<AdminRateLimit[]> => request('/api/admin/rate-limits'),

    updateRateLimit: (
      endpoint: string,
      scope: string,
      patch: { windowSeconds?: number; maxRequests?: number; enabled?: boolean },
    ): Promise<AdminRateLimit> =>
      request(`/api/admin/rate-limits/${endpoint}/${scope}`, { method: 'PUT', ...json(patch) }),

    listRequestLimits: (): Promise<AdminRequestLimit[]> =>
      request('/api/admin/request-limits'),

    updateRequestLimit: (
      endpoint: string,
      patch: { maxItems: number },
    ): Promise<AdminRequestLimit> =>
      request(`/api/admin/request-limits/${endpoint}`, { method: 'PUT', ...json(patch) }),

    listAnomalyFlags: (resolved = false): Promise<AdminAnomalyFlag[]> =>
      request(`/api/admin/anomaly/flags?resolved=${resolved}`),

    resolveAnomalyFlag: (id: string): Promise<{ ok: true; resolvedAt: string }> =>
      request(`/api/admin/anomaly/flags/${id}/resolve`, { method: 'POST' }),

    runAnomalyDetectors: (): Promise<AnomalyRunSummary> =>
      request('/api/admin/anomaly/run-now', { method: 'POST' }),

    setUserRole: (id: string, role: UserRole): Promise<{ ok: true; id: string; role: UserRole; email: string }> =>
      request(`/api/admin/users/${id}/role`, { method: 'POST', ...json({ role }) }),

    listAlerts: (): Promise<AdminAlertConfig[]> => request('/api/admin/alerts'),

    updateAlert: (
      category: AlertCategory,
      patch: {
        enabled?: boolean;
        threshold?: Record<string, unknown>;
        channels?: { webhookUrl?: string; emailTo?: string; telegramChatId?: string };
      },
    ): Promise<AdminAlertConfig> =>
      request(`/api/admin/alerts/${category}`, { method: 'PATCH', ...json(patch) }),

    testAlert: (category: AlertCategory, channel: AlertChannel): Promise<AdminAlertTestResult> =>
      request(`/api/admin/alerts/${category}/test`, { method: 'POST', ...json({ channel }) }),

    catalog: {
      listCategories: (): Promise<CatalogCategory[]> =>
        request('/api/admin/catalog/categories'),

      createCategory: (data: { name: string; color?: string | null; sortOrder?: number }): Promise<CatalogCategory> =>
        request('/api/admin/catalog/categories', { method: 'POST', ...json(data) }),

      updateCategory: (
        id: string,
        patch: { name?: string; color?: string | null; sortOrder?: number },
      ): Promise<CatalogCategory> =>
        request(`/api/admin/catalog/categories/${id}`, { method: 'PATCH', ...json(patch) }),

      deleteCategory: (id: string): Promise<{ ok: true }> =>
        request(`/api/admin/catalog/categories/${id}`, { method: 'DELETE' }),

      mergeCategory: (fromId: string, intoId: string): Promise<{ ok: true }> =>
        request(`/api/admin/catalog/categories/${fromId}/merge`, { method: 'POST', ...json({ intoId }) }),

      listEntries: (params?: {
        q?: string;
        category?: string;
        featured?: 'true' | 'false';
        needsMeta?: 'true';
        linkHealth?: 'dead' | 'unknown' | 'ok' | 'unchecked';
        limit?: number;
        offset?: number;
      }): Promise<{ items: CatalogEntry[]; total: number }> => {
        const qs = new URLSearchParams();
        if (params?.q) qs.set('q', params.q);
        if (params?.category) qs.set('category', params.category);
        if (params?.featured) qs.set('featured', params.featured);
        if (params?.needsMeta) qs.set('needsMeta', params.needsMeta);
        if (params?.linkHealth) qs.set('linkHealth', params.linkHealth);
        if (params?.limit != null) qs.set('limit', String(params.limit));
        if (params?.offset != null) qs.set('offset', String(params.offset));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return request(`/api/admin/catalog/entries${suffix}`);
      },

      createEntry: (data: {
        url: string;
        title?: string;
        description?: string;
        categoryId?: string | null;
        isFeatured?: boolean;
      }): Promise<CatalogEntry> =>
        request('/api/admin/catalog/entries', { method: 'POST', ...json(data) }),

      importEntries: (
        items: Array<{
          url: string;
          title?: string;
          description?: string;
          categoryName?: string;
        }>,
      ): Promise<{ imported: number; skipped: number } | { jobId: string }> =>
        request('/api/admin/catalog/entries/import', { method: 'POST', ...json({ items }) }),

      scrapeUrl: (
        url: string,
      ): Promise<{
        items: Array<{
          url: string;
          title: string;
          description?: string;
          categoryName?: string;
        }>;
        sourceUrl: string;
        warnings: string[];
      }> =>
        request('/api/admin/catalog/scrape-url', { method: 'POST', ...json({ url }) }),

      updateEntry: (
        id: string,
        patch: {
          title?: string | null;
          description?: string | null;
          categoryId?: string | null;
          isFeatured?: boolean;
          source?: 'manual' | 'crowdsourced';
        },
      ): Promise<CatalogEntry> =>
        request(`/api/admin/catalog/entries/${id}`, { method: 'PATCH', ...json(patch) }),

      deleteEntry: (id: string): Promise<{ ok: true }> =>
        request(`/api/admin/catalog/entries/${id}`, { method: 'DELETE' }),

      bulkDeleteEntries: (ids: string[]): Promise<{ deleted: number }> =>
        request('/api/admin/catalog/entries/bulk-delete', { method: 'POST', ...json({ ids }) }),

      refetchEntry: (id: string): Promise<{ ok: true }> =>
        request(`/api/admin/catalog/entries/${id}/refetch`, { method: 'POST' }),

      // MF-211 — re-check link health now (single / bulk) + drain a stale batch.
      recheckEntry: (id: string): Promise<{ ok: true }> =>
        request(`/api/admin/catalog/entries/${id}/recheck`, { method: 'POST' }),

      recheckEntries: (ids: string[]): Promise<{ checked: number }> =>
        request('/api/admin/catalog/entries/recheck', { method: 'POST', ...json({ ids }) }),

      linkCheckSweep: (): Promise<{ checked: number }> =>
        request('/api/admin/catalog/link-check-sweep', { method: 'POST' }),

      stats: (): Promise<{
        total: number;
        metaPending: number;
        metaFailed: number;
        embedReady: number;
        embedPending: number;
        embedFailed: number;
        uncategorized: number;
        featured: number;
        needsMetadata: number;
        linkOk: number;
        linkDead: number;
        linkUnknown: number;
        linkUnchecked: number;
      }> => request('/api/admin/catalog/stats'),

      titleSuggestions: (id: string): Promise<{ items: CatalogTitleSuggestion[] }> =>
        request(`/api/admin/catalog/entries/${id}/title-suggestions`),

      auditLog: (id: string): Promise<{ items: CatalogMetadataAudit[] }> =>
        request(`/api/admin/catalog/entries/${id}/audit`),

      embedSweep: (): Promise<{ embedded: number; failed: number; skipped: number }> =>
        request('/api/admin/catalog/embed-sweep', { method: 'POST' }),

      reembedAll: (): Promise<{ queued: number; pending: number }> =>
        request('/api/admin/catalog/reembed-all', { method: 'POST' }),

      categorize: (): Promise<{
        scanned: number;
        attached: number;
        newCategories: number;
        unassigned: number;
        modelMismatchSkipped: number;
      }> => request('/api/admin/catalog/categorize', { method: 'POST' }),
    },

    getAppSettingsFull: (): Promise<Array<{ key: string; value: unknown; updatedAt: string; updatedBy: string | null }>> =>
      request('/api/admin/app-settings'),

    setAppSetting: (key: string, value: object): Promise<{ ok: true }> =>
      request(`/api/admin/app-settings/${encodeURIComponent(key)}`, { method: 'PUT', ...json({ value }) }),

    // MF-199 — feature flags admin.
    getFeatureFlags: (): Promise<Array<{
      key: string;
      mode: 'off' | 'admin' | 'allowlist' | 'on';
      description: string | null;
      allowlistCount: number;
      updatedAt: string;
    }>> => request('/api/admin/feature-flags'),

    setFeatureFlag: (
      key: string,
      patch: { mode?: 'off' | 'admin' | 'allowlist' | 'on'; description?: string | null },
    ): Promise<{
      key: string;
      mode: 'off' | 'admin' | 'allowlist' | 'on';
      description: string | null;
      updatedAt: string;
    }> =>
      request(`/api/admin/feature-flags/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        ...json(patch),
      }),

    getFeatureFlagAllowlist: (key: string): Promise<Array<{
      userId: string;
      email: string | null;
      name: string | null;
      addedAt: string;
    }>> => request(`/api/admin/feature-flags/${encodeURIComponent(key)}/allowlist`),

    addFeatureFlagAllowlistUser: (
      key: string,
      userId: string,
    ): Promise<{ userId: string; email: string | null; name: string | null }> =>
      request(`/api/admin/feature-flags/${encodeURIComponent(key)}/allowlist`, {
        method: 'POST',
        ...json({ userId }),
      }),

    removeFeatureFlagAllowlistUser: (key: string, userId: string): Promise<{ ok: true }> =>
      request(
        `/api/admin/feature-flags/${encodeURIComponent(key)}/allowlist/${encodeURIComponent(userId)}`,
        { method: 'DELETE' },
      ),

    searchUsers: (q: string): Promise<Array<{ id: string; email: string; name: string | null }>> =>
      request(`/api/admin/users-search?q=${encodeURIComponent(q)}`),

    getCategorizationMetrics: (days: number = 7): Promise<{
      days: number;
      counts: Record<'auto_place' | 'llm_chose' | 'suggest_new' | 'ambiguous', number>;
      prevCounts: Record<'auto_place' | 'llm_chose' | 'suggest_new' | 'ambiguous', number>;
      daily: Array<{
        day: string;
        auto_place: number;
        llm_chose: number;
        suggest_new: number;
        ambiguous: number;
      }>;
      total: number;
      llmInvocationRate: number;
    }> => request(`/api/admin/categorization/metrics?days=${days}`),

    getCategorizationRecent: (limit: number = 50, offset: number = 0): Promise<{
      rows: Array<{
        id: string;
        userId: string;
        decision: string;
        userOverrode: boolean;
        chosenFolderId: string | null;
        chosenFolderName: string | null;
        chosenFolderColor: string | null;
        userChoseFolderId: string | null;
        userChoseFolderName: string | null;
        userChoseFolderColor: string | null;
        bookmarkId: string | null;
        bookmarkTitle: string | null;
        bookmarkUrl: string | null;
        bookmarkFolderId: string | null;
        llmReasoning: string | null;
        createdAt: string;
        userEmail: string | null;
        userName: string | null;
        userAvatarUrl: string | null;
      }>;
      limit: number;
      offset: number;
    }> => request(`/api/admin/categorization/recent?limit=${limit}&offset=${offset}`),

    // MF-168 — AI tag-suggest usage stats (mirrors the categorization metrics).
    getTagSuggestMetrics: (days: number = 7): Promise<{
      days: number;
      counts: Record<'suggested' | 'applied' | 'dismissed', number>;
      prevCounts: Record<'suggested' | 'applied' | 'dismissed', number>;
      daily: Array<{ day: string; suggested: number; applied: number; dismissed: number }>;
      total: number;
      acceptanceRate: number;
      autoApplied: number;
    }> => request(`/api/admin/tag-suggest/metrics?days=${days}`),

    // MF-169 — thumbs-down feedback distribution (feature × provider × decision).
    getSuggestionFeedbackMetrics: (days: number = 7): Promise<{
      days: number;
      total: number;
      byFeature: Record<string, number>;
      breakdown: Array<{ feature: string; provider: string; decision: string; n: number }>;
    }> => request(`/api/admin/suggestion-feedback/metrics?days=${days}`),

    getTagSuggestRecent: (limit: number = 50, offset: number = 0): Promise<{
      rows: Array<{
        id: string;
        userId: string;
        decision: string;
        autoApplied: boolean;
        suggestedCount: number;
        appliedCount: number;
        bookmarkId: string | null;
        bookmarkTitle: string | null;
        bookmarkUrl: string | null;
        bookmarkFolderId: string | null;
        createdAt: string;
        userEmail: string | null;
        userName: string | null;
        userAvatarUrl: string | null;
      }>;
      limit: number;
      offset: number;
    }> => request(`/api/admin/tag-suggest/recent?limit=${limit}&offset=${offset}`),

    getCentroidStatus: (): Promise<{
      total: number;
      withCentroid: number;
      withDescVec: number;
      oldestVectorsUpdatedAt: string | null;
      lastDriftRunAt: string | null;
      lastDriftRecomputed: number;
    }> => request('/api/admin/categorization/centroid-status'),

    recomputeCentroidsNow: (): Promise<{
      recomputed: number;
      durationMs: number;
      lastDriftRunAt: string;
    }> => request('/api/admin/categorization/recompute-now', { method: 'POST' }),

    getEmbeddingStatus: (): Promise<{
      totalBookmarks: number;
      withEmbedding: number;
      totalFoldersWithDescription: number;
      withDescriptionVector: number;
      lastBackfillAt: string | null;
      lastBackfillRecomputedBookmarks: number;
    }> => request('/api/admin/categorization/embedding-status'),

    backfillEmbeddings: (): Promise<{
      bookmarksEmbedded: number;
      foldersDescEmbedded: number;
      foldersRecomputed: number;
      remainingBookmarks: number;
      remainingFolders: number;
      durationMs: number;
      lastBackfillAt: string;
    }> => request('/api/admin/categorization/backfill-embeddings', { method: 'POST' }),

    // MF-81 — per-feature AI settings (baseline). Returns `null` when no row
    // exists yet so the caller can seed from DEFAULT_AI_FEATURE_SETTINGS.
    getAiFeatureSettings: (): Promise<AiFeatureSettingsMap | null> =>
      request<Array<{ key: string; value: unknown }>>('/api/admin/app-settings').then((rows) => {
        const row = rows.find((r) => r.key === 'ai_feature_settings');
        return (row?.value as AiFeatureSettingsMap) ?? null;
      }),

    setAiFeatureSettings: (next: AiFeatureSettingsMap): Promise<{ ok: true }> =>
      request('/api/admin/app-settings/ai_feature_settings', {
        method: 'PUT',
        ...json({ value: next }),
      }),

    setProviderOverrides: (
      providerId: string,
      settingsOverrides: ProviderSettingsOverrides,
    ): Promise<AdminProvider> =>
      request(`/api/admin/ai-providers/${providerId}`, {
        method: 'PUT',
        ...json({ settingsOverrides }),
      }),

    // MF-198 — chat-cost dashboard. Aggregates op='chat_rag' rows from
    // ai_usage_log over a date range, grouped by user/provider/model.
    getChatCosts: (params: {
      from?: string;
      to?: string;
      dim: 'user' | 'provider' | 'model';
    }): Promise<{
      dim: 'user' | 'provider' | 'model';
      from: string;
      to: string;
      totalUnits: number;
      totalRequests: number;
      items: Array<{
        key: string;
        label: string | null;
        totalUnits: number;
        totalProviderUnits: number;
        requestCount: number;
        distinctUsers: number;
        distinctConversations: number;
        lastUsedAt: string;
      }>;
    }> => {
      const qs = new URLSearchParams();
      qs.set('dim', params.dim);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      return request(`/api/admin/chat-costs?${qs.toString()}`);
    },
  },

  account: {
    listTransactions: (params?: { limit?: number; offset?: number }): Promise<{ rows: AccountTransaction[]; total: number }> => {
      const qs = new URLSearchParams();
      if (params?.limit != null) qs.set('limit', String(params.limit));
      if (params?.offset != null) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request(`/api/account/transactions${suffix}`);
    },
    getBalance: (): Promise<TokenBalance> => request('/api/account/balance'),
  },

  tokens: {
    list: (): Promise<ApiTokenSummary[]> => request('/api/tokens'),
    create: (name: string, scopes?: ApiTokenScope[]): Promise<ApiTokenCreated> =>
      request('/api/tokens', { method: 'POST', ...json({ name, scopes: scopes ?? [] }) }),
    revoke: (id: string): Promise<{ ok: true }> =>
      request(`/api/tokens/${id}`, { method: 'DELETE' }),
  },

  extension: {
    authorize: (name?: string): Promise<ApiTokenCreated> =>
      request('/api/extension/authorize', { method: 'POST', ...json({ name }) }),
  },

  extensionReleases: {
    latest: (): Promise<LatestExtensionReleases> =>
      request('/api/extension-releases/latest'),
  },

  htmlImport: {
    preview: (
      file: File,
      variant?: HtmlImportVariant | 'auto',
      spaceId?: string,
    ): Promise<HtmlImportPreview> => {
      const fd = new FormData();
      fd.append('file', file);
      if (variant) fd.append('variant', variant);
      // markflow-v1 conflict estimate is scoped to this target Space.
      if (spaceId) fd.append('spaceId', spaceId);
      return request('/api/html-import/preview', { method: 'POST', body: fd });
    },
    start: (
      file: File,
      variant?: HtmlImportVariant | 'auto',
      spaceId?: string,
      conflictPolicy?: 'skip-dupes' | 'update' | 'suffix',
    ): Promise<{ jobId: string }> => {
      const fd = new FormData();
      fd.append('file', file);
      if (variant) fd.append('variant', variant);
      if (spaceId) fd.append('spaceId', spaceId);
      if (conflictPolicy) fd.append('conflictPolicy', conflictPolicy);
      // Intentionally no Content-Type header — the browser sets multipart
      // boundary itself when body is FormData.
      return request('/api/html-import', { method: 'POST', body: fd });
    },
    list: (): Promise<{ jobs: HtmlImportJob[] }> => request('/api/html-import'),
    get: (jobId: string): Promise<HtmlImportJob> => request(`/api/html-import/${jobId}`),
    cancel: (jobId: string): Promise<{ ok: true }> =>
      request(`/api/html-import/${jobId}/cancel`, { method: 'POST' }),
  },

  // MF-109 — Cleanup Suite: dead-link checker + empty-folder scanner.
  cleanup: {
    linkCheck: (spaceId?: string): Promise<{ queued: number }> => {
      const qs = spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : '';
      return request(`/api/cleanup/link-check${qs}`, { method: 'POST' });
    },
    deadLinks: (params: { deadness?: string; status?: string; spaceId?: string } = {}): Promise<DeadLinksResponse> => {
      const q = new URLSearchParams();
      if (params.deadness) q.set('deadness', params.deadness);
      if (params.status) q.set('status', params.status);
      if (params.spaceId) q.set('spaceId', params.spaceId);
      const qs = q.toString();
      return request<DeadLinksResponse>(`/api/cleanup/dead-links${qs ? `?${qs}` : ''}`);
    },
    dismissDeadLink: (id: string): Promise<{ ok: true }> =>
      request(`/api/cleanup/dead-links/${id}/dismiss`, { method: 'POST' }),
    // MF-127 — per-user Wayback archive mode (on_death vs always/Pro).
    getWaybackMode: (): Promise<{ mode: WaybackMode }> => request('/api/me/wayback'),
    setWaybackMode: (mode: WaybackMode): Promise<{ mode: WaybackMode }> =>
      request('/api/me/wayback', { method: 'PUT', ...json({ mode }) }),
    emptyFolders: (mode: 'strict' | 'effective', spaceId?: string): Promise<EmptyFoldersResponse> => {
      const q = new URLSearchParams({ mode });
      if (spaceId) q.set('spaceId', spaceId);
      return request<EmptyFoldersResponse>(`/api/cleanup/empty-folders?${q.toString()}`);
    },
  },

  // MF-102 — duplicate detection + cleanup. Apply/Undo reuse the bulk-tag +
  // bulk-delete + create endpoints client-side (see DuplicateGroupCard).
  duplicates: {
    // spaceId omitted/null → "All Spaces"; a Space id scopes the scan + cache.
    get: (spaceId?: string | null): Promise<DuplicatesResponse> =>
      request(`/api/duplicates${spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : ''}`),
    recompute: (spaceId?: string | null): Promise<{ jobId: string }> =>
      request('/api/duplicates/recompute', { method: 'POST', ...json({ spaceId: spaceId ?? null }) }),
  },
};
